import type { FunctionComponent } from 'react'
import React, { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { Heading, PageTitle, Picture, TypographyV2 as Typography } from '@which/seatbelt'

import classNames from 'classnames'

import { ImageWithQuestion } from '../../components/ImageWithQuestion'
import { NextButton } from '../../components/NextButton'
import { ToolContext } from '../../state/appContext'
import type { CRToolEntryPointProps, variantDataProps } from '../../types/toolTypes'
import { parseJson } from '../../utils/parseJson/parseJson'
import { generalDataLayer } from '../../utils/tracking'
import { validEntryOptions } from '../../utils/validEntryOptions/validEntryOptions'
import styles from './CRToolEntryPoint.module.scss'
import { CRToolEntryData } from './data'

export const CRToolEntryPoint: FunctionComponent<Props> = ({
  optionsJson,
  toolName,
  buttonText,
  questionText,
  standfirst,
  title,
  toolUrl,
}) => {
  const { state, dispatch } = useContext(ToolContext)
  const [selection, setSelection] = useState('product')
  const [external, setExternal] = useState(false)

  const history = useHistory()

  useEffect(() => {
    dispatch({ type: 'UPDATE_PROGRESS_VALUE', data: 0 })
  }, [dispatch])

  if (
    (optionsJson && !validEntryOptions(parseJson(optionsJson))) ||
    !Array.isArray(parseJson(standfirst))
  ) {
    return null
  }

  let href = toolUrl
  const options: CRToolEntryPointProps = optionsJson ? parseJson(optionsJson) : []

  if (optionsJson && options.length > 0) {
    const chosenOption = options.filter(({ value }) => value === selection)
    if (chosenOption[0].external) {
      href = chosenOption[0].link
      if (!external) {
        setExternal(true)
      }
    } else {
      href = `?type=${chosenOption[0].link}`
      if (external) {
        setExternal(false)
      }
    }
  }

  const standfirstArray: string[] = parseJson(standfirst)

  const handleClick = () => {
    if (!external) {
      history.push(href, { ...state })
      const trackingAnswer = `question | product-type | ${selection}`
      generalDataLayer(toolName, trackingAnswer, false)
      dispatch({ type: 'UPDATE_PRODUCT_TYPE', data: selection })
    } else {
      history.push(href, null)
      location.reload()
    }
  }

  const renderDropdown = ({ label }: { label: string }) => {
    if (options.length > 0) {
      return (
        <>
          <label className="sr-only" htmlFor="tool-choice">
            {label}
          </label>
          <select
            className={classNames(styles.CRToolEntryPointDropdown, {
              [styles.CRToolEntryPointDropdownVariant]: toolName === 'faulty goods',
            })}
            data-testid="cr-tool-datalist-items"
            id="tool-choice"
            name="tool-choice"
            onChange={(e) => {
              setSelection(e.target.value)
            }}
          >
            {options.map((option) => {
              const { label: optionText, value } = option
              return (
                <option key={value} value={value}>
                  {optionText}
                </option>
              )
            })}
          </select>
        </>
      )
    }
  }

  const renderStartComplaint = () => {
    return (
      <div className={styles.CRToolEntryPointButton}>
        <NextButton
          buttonText={buttonText}
          data-testid="cr-tool-link"
          moneySize={toolName === 'MyMoneyHealthCheck'}
          onClick={handleClick}
        />
      </div>
    )
  }

  const renderCRToolEntryPoint = () => {
    if (toolName === 'faulty goods') {
      return renderVariant()
    }
    return renderOriginal()
  }

  const renderOriginal = (variant = false) => {
    if (optionsJson) {
      return (
        <div
          className={classNames({
            [styles.CRToolEntryPoint]: !variant,
          })}
        >
          <Heading heading={title} headingTag="h3" headingType="medium" />
          {standfirstArray?.map((standfirstItem) => {
            return (
              <Typography key={standfirstItem} textStyle="sb-text-body-default-regular">
                {standfirstItem}
              </Typography>
            )
          })}

          <Typography
            className={styles.CRToolEntryPointLabel}
            tag="label"
            textStyle="sb-text-body-default-strong"
          >
            {questionText}
            {renderDropdown({ label: questionText })}
          </Typography>
          {renderStartComplaint()}
        </div>
      )
    }
  }

  const renderSteps = (steps: { strapline: string; image: { src: string; alt: string } }[]) => {
    return steps.map((step, index) => {
      const { strapline, image } = step

      return (
        <div className={styles.CRToolEntryPointStep} key={`step-${index}`}>
          <Picture
            className={classNames(styles.CRToolEntryPointStepImage)}
            lazyLoad={false}
            {...image}
          />
          <Typography data-testid="typography-body" textStyle="sb-text-body-small-regular">
            {strapline}
          </Typography>
        </div>
      )
    })
  }

  const renderStrapLine = ({ text }: { text: string }) => (
    <Typography
      className={classNames(styles.CRToolEntryPointStrapline)}
      dangerouslySetInnerHTML={{ __html: text }}
      key={text}
      textStyle="sb-text-body-default-regular"
    />
  )

  const renderVariant = () => {
    const variantData: variantDataProps = CRToolEntryData.faultyGoods
    return (
      <div className={classNames(styles.CRToolEntryPointVariant)}>
        <PageTitle pageTitle={variantData.title} pageTitleTag="h1" />
        {variantData.straplines.map((strapline) => renderStrapLine({ ...strapline }))}
        <div className={styles.CRToolEntryPointSteps}>{renderSteps(variantData.calloutSteps)}</div>
        {variantData?.image && <ImageWithQuestion {...variantData.image} />}
        {renderDropdown({ label: questionText })}
        {renderStartComplaint()}
      </div>
    )
  }

  return <div data-testid="cr-tool-entry-point">{renderCRToolEntryPoint()}</div>
}

type Props = {
  optionsJson: string
  toolName: string
  buttonText: string
  questionText: string
  standfirst: string
  title: string
  toolUrl: string
}
